import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { openImageEditDialog } from "state/imageeditdialog/actions";
import { useParent, useDispatcher } from "ui/hook";
import { Icon, msMoreVert12 } from "ui/material-symbols";
import { DropdownMenu } from "ui/control";
import { useRichTextEditorMedia } from "ui/control/richtexteditor/media/rich-text-editor-media-context";
import { useRichTextEditorCommands } from "ui/control/richtexteditor/rich-text-editor-commands-context";
import AttachedImage from "ui/control/richtexteditor/media/AttachedImage";
import { MediaWithCaption } from "util/media-with-caption";
import { REL_CURRENT, RelNodeName } from "util/rel-node-name";
import "./UploadedImage.css";

interface Props {
    media: MediaWithCaption;
    nodeName: RelNodeName | string;
    dragged?: boolean | null;
    showMenu?: boolean | null;
    noEmbeddedMedia?: boolean | null;
}

export default function UploadedImage({media, nodeName, dragged = false, showMenu = true, noEmbeddedMedia}: Props) {
    const sortable = useSortable({id: media.mediaId ?? ""});
    const sortableStyle = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition ?? undefined,
    };
    const {deleteMedia} = useRichTextEditorMedia();
    const {embedImage} = useRichTextEditorCommands();
    const dispatch = useDispatcher();
    const {t} = useTranslation();

    const {overlayId: parentOverlayId} = useParent();

    const onEdit = () => {
        if (!dragged) {
            dispatch(openImageEditDialog(nodeName, media, parentOverlayId));
        }
    }

    return (
        <div className={cx("rich-text-editor-uploaded-image", {"dragged": dragged})}>
            {showMenu &&
                <DropdownMenu items={[
                    {
                        title: t("edit"),
                        nodeName: REL_CURRENT,
                        href: "/",
                        onClick: onEdit,
                        show: true
                    },
                    {
                        title: t("insert-into-text"),
                        nodeName: REL_CURRENT,
                        href: "/",
                        onClick: () => embedImage(media),
                        show: !noEmbeddedMedia
                    },
                    {
                        divider: true
                    },
                    {
                        title: t("delete"),
                        nodeName: REL_CURRENT,
                        href: "/",
                        onClick: () => media.mediaId && deleteMedia(media.mediaId),
                        show: true
                    }
                ]} menuContainer={document.getElementById("modal-root")}>
                    <Icon icon={msMoreVert12} size={12}/>
                </DropdownMenu>
            }
            <div ref={sortable.setNodeRef} style={sortableStyle} {...sortable.attributes} {...sortable.listeners}>
                <AttachedImage media={media} nodeName={nodeName} onClick={onEdit}/>
            </div>
        </div>
    );
}
